import React, { useState } from "react";

const budgetOptions = [
  "Less Than 1 Lakhs",
  "3 Lakhs - 5 Lakhs",
  "5 Lakhs - 10 Lakhs",
  "More Than 10 Lakhs",
];

const defaultSourceOptions = [
  "Service page",
  "Referral",
  "Search",
  "Social media",
  "Other",
];

const fieldClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#FF6B00] focus:ring-4 focus:ring-[#FF6B0012]";

const labelClassName = "mb-2 block text-sm font-medium text-slate-700 font-poppins";

const initialFormState = (defaultSource) => ({
  name: "",
  email: "",
  phone: "",
  website: "",
  budget: "",
  source: defaultSource,
  message: "",
});

const ServiceInquiryForm = ({
  title,
  description,
  submitLabel = "Send Enquiry",
  serviceName = "Service Inquiry",
  sourceOptions = defaultSourceOptions,
  defaultSource,
  websiteLabel = "Website or App URL",
  messageLabel = "Project Details",
  messagePlaceholder = "Tell us what you want to build, automate, or improve.",
  successTitle = "Thanks, we received your details.",
  successDescription = "Our team will review your enquiry and reply shortly.",
}) => {
  const startingSource = defaultSource || sourceOptions[0] || "Service page";
  const fieldPrefix = serviceName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "service-inquiry";
  const availableSourceOptions = sourceOptions.includes(startingSource)
    ? sourceOptions
    : [startingSource, ...sourceOptions];
  const [form, setForm] = useState(() => initialFormState(startingSource));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) nextErrors.name = "Name is required";

    if (!form.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address";
    }

    if (!form.phone.trim()) nextErrors.phone = "Phone number is required";
    if (!form.website.trim()) nextErrors.website = "Website or app URL is required";
    if (!form.budget) nextErrors.budget = "Select a budget range";
    if (!form.source) nextErrors.source = "Select how you found us";
    if (!form.message.trim()) nextErrors.message = "Tell us a little about your project";

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));

    setErrors((current) => ({
      ...current,
      [name]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      const response = await fetch("/send_mail.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "contact-form",
          service: serviceName,
          ...form,
        }),
      });

      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();

        if (response.ok && (text.includes("simulated") || text.includes("Logged"))) {
          data = { success: true };
        } else {
          throw new Error("Server error or PHP not configured properly");
        }
      }

      if (!data?.success) {
        throw new Error(data?.message || "Submission failed");
      }

      setSuccess(true);
    } catch (error) {
      setSubmitError("Something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="rounded-[32px] border border-[#FF6B0020] bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FF6B0012] text-2xl text-[#FF6B00]">
          ✓
        </div>
        <h3 className="mt-6 text-2xl font-semibold text-slate-900 font-unbounded">
          {successTitle}
        </h3>
        <p className="mt-3 text-sm leading-7 text-slate-600 font-poppins">
          {successDescription}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-[32px] border border-[#FF6B0020] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)] md:p-8">
      <div className="space-y-2">
        <h3 className="text-2xl font-semibold text-slate-900 font-unbounded">
          {title}
        </h3>
        {description ? (
          <p className="text-sm leading-7 text-slate-600 font-poppins">
            {description}
          </p>
        ) : null}
      </div>

      {submitError ? (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 font-poppins">
          {submitError}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className={labelClassName} htmlFor={`${fieldPrefix}-name`}>
              Full Name
            </label>
            <input
              id={`${fieldPrefix}-name`}
              name="name"
              value={form.name}
              onChange={handleChange}
              className={fieldClassName}
              placeholder="Your name"
            />
            {errors.name ? <p className="mt-1 text-xs text-red-500">{errors.name}</p> : null}
          </div>

          <div>
            <label className={labelClassName} htmlFor={`${fieldPrefix}-email`}>
              Email Address
            </label>
            <input
              id={`${fieldPrefix}-email`}
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className={fieldClassName}
              placeholder="you@company.com"
            />
            {errors.email ? <p className="mt-1 text-xs text-red-500">{errors.email}</p> : null}
          </div>

          <div>
            <label className={labelClassName} htmlFor={`${fieldPrefix}-phone`}>
              Phone Number
            </label>
            <input
              id={`${fieldPrefix}-phone`}
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className={fieldClassName}
              placeholder="Your phone number"
            />
            {errors.phone ? <p className="mt-1 text-xs text-red-500">{errors.phone}</p> : null}
          </div>

          <div>
            <label className={labelClassName} htmlFor={`${fieldPrefix}-website`}>
              {websiteLabel}
            </label>
            <input
              id={`${fieldPrefix}-website`}
              name="website"
              value={form.website}
              onChange={handleChange}
              className={fieldClassName}
              placeholder="https://example.com"
            />
            {errors.website ? <p className="mt-1 text-xs text-red-500">{errors.website}</p> : null}
          </div>

          <div>
            <label className={labelClassName} htmlFor={`${fieldPrefix}-budget`}>
              Budget Range
            </label>
            <select
              id={`${fieldPrefix}-budget`}
              name="budget"
              value={form.budget}
              onChange={handleChange}
              className={fieldClassName}
            >
              <option value="">Select budget</option>
              {budgetOptions.map((budgetOption) => (
                <option key={budgetOption} value={budgetOption}>
                  {budgetOption}
                </option>
              ))}
            </select>
            {errors.budget ? <p className="mt-1 text-xs text-red-500">{errors.budget}</p> : null}
          </div>

          <div>
            <label className={labelClassName} htmlFor={`${fieldPrefix}-source`}>
              How did you find us?
            </label>
            <select
              id={`${fieldPrefix}-source`}
              name="source"
              value={form.source}
              onChange={handleChange}
              className={fieldClassName}
            >
              <option value="">Select source</option>
              {availableSourceOptions.map((sourceOption) => (
                <option key={sourceOption} value={sourceOption}>
                  {sourceOption}
                </option>
              ))}
            </select>
            {errors.source ? <p className="mt-1 text-xs text-red-500">{errors.source}</p> : null}
          </div>
        </div>

        <div>
          <label className={labelClassName} htmlFor={`${fieldPrefix}-message`}>
            {messageLabel}
          </label>
          <textarea
            id={`${fieldPrefix}-message`}
            name="message"
            value={form.message}
            onChange={handleChange}
            rows="5"
            className={fieldClassName}
            placeholder={messagePlaceholder}
          />
          {errors.message ? <p className="mt-1 text-xs text-red-500">{errors.message}</p> : null}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="group relative flex w-full items-center justify-center overflow-hidden rounded-full bg-[#FF6B00] px-6 py-4 text-base font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <span className="relative z-10 font-unbounded text-sm md:text-base">
            {submitting ? "Submitting..." : submitLabel}
          </span>
          <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E85D00] transition-all duration-500 group-hover:h-[22rem] group-hover:w-[22rem]" />
        </button>
      </form>
    </div>
  );
};

export default ServiceInquiryForm;
